import type { Result } from "neverthrow";
import { err, ok } from "neverthrow";
import { v4 as uuidv4, validate as validatev4 } from "uuid";
import * as z from "zod";

const IdSchema = z
    .union([z.int().positive(), z.null().transform(() => uuidv4())])
    .transform((value) => ({ value: value.toString() }))
    .brand<"Id">();

export type IdInputProps = z.input<typeof IdSchema>;
type IdProps = z.output<typeof IdSchema>;

/**
 * Id is a value object that represents an id.
 * You have to provide an id number when the model is persisted or null when it is not to generate an uuid.
 */
// eslint-disable-next-line id-length
export class Id {
    get value(): IdProps["value"] {
        return this.props.value;
    }

    /**
     * Use this method only for models that are persisted.
     * Prefer this method over the value property to enforce the use of a persisted id where that is expected
     * @throws Error if the value is not a valid number
     */
    get valueOrThrow(): number {
        const maybeNumber = Number(this.props.value);

        if (isNaN(maybeNumber)) {
            throw new Error("Invalid id");
        }

        return maybeNumber;
    }

    private constructor(private readonly props: Readonly<IdProps>) {}

    static validate(props: unknown): Result<IdProps, z.ZodError<IdProps>> {
        const result = IdSchema.safeParse(props);

        return result.success ? ok(result.data) : err(result.error);
    }

    static create(value: IdInputProps): Result<Id, z.ZodError<IdProps>> {
        return Id.validate(value).map((props) => new Id(props));
    }

    toCreateProps(): IdInputProps {
        return validatev4(this.props.value) ? null : Number(this.props.value);
    }

    equals(id: Id): boolean {
        return this.props.value === id.props.value;
    }
}

export const zIdSchema = z
    .union([z.int().positive(), z.null()])
    .transform((value, ctx) =>
        Id.create(value).match(
            (id) => id,
            (error) => {
                error.issues.forEach((issue) => {
                    ctx.addIssue({
                        ...issue,
                    });
                });

                return z.NEVER;
            },
        ),
    );
